import { useEffect, useState } from "react";
import { History, Save, RotateCcw, CheckCircle2, Eye } from "lucide-react";
import { adminApi } from "./api";
import { toast } from "sonner";

function fmtVal(v) {
  if (v === undefined || v === null || v === "") return "(empty)";
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// Flat key-level diff between a historical version and the current draft —
// enough to show admins what a "Restore" would actually change.
function diffFields(oldObj = {}, newObj = {}) {
  const keys = [...new Set([...Object.keys(oldObj), ...Object.keys(newObj)])];
  return keys
    .filter((k) => JSON.stringify(oldObj[k]) !== JSON.stringify(newObj[k]))
    .map((k) => ({ key: k, from: fmtVal(newObj[k]), to: fmtVal(oldObj[k]) }));
}

/**
 * Generic draft/publish/version-history chrome for one (page, section) CMS
 * document. Renders custom fields via the `renderFields` render-prop.
 */
export default function CmsSectionEditor({ page, section, title, description, renderFields }) {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/cms/${page}/${section}`);
      setDoc(r.data);
      setDraft(r.data.draft && Object.keys(r.data.draft).length > 0 ? r.data.draft : r.data.published || {});
    } catch {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, section]);

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const saveDraft = async (silent = false) => {
    setSaving(true);
    try {
      const r = await adminApi.put(`/cms/${page}/${section}`, { content: draft });
      setDoc(r.data);
      if (!silent) toast.success("Draft saved");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const ok = await saveDraft(true);
      if (!ok) return;
      const r = await adminApi.post(`/cms/${page}/${section}/publish`);
      setDoc(r.data);
      toast.success("Published — live on the website");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const discard = async () => {
    if (!window.confirm("Discard unpublished changes for this section?")) return;
    try {
      const r = await adminApi.post(`/cms/${page}/${section}/discard`);
      setDoc(r.data);
      setDraft(r.data.draft || {});
      toast.success("Draft discarded");
    } catch {
      toast.error("Discard failed");
    }
  };

  const restore = async (index) => {
    if (!window.confirm("Restore this version into your draft? You'll still need to Publish it.")) return;
    try {
      const r = await adminApi.post(`/cms/${page}/${section}/restore`, { index });
      setDoc(r.data);
      setDraft(r.data.draft || {});
      setShowHistory(false);
      toast.success("Version restored to draft — review then Publish");
    } catch {
      toast.error("Restore failed");
    }
  };

  if (loading || !draft) {
    return (
      <div className="text-sm py-10 text-center" style={{ color: "var(--ax-text-muted)" }}>
        Loading…
      </div>
    );
  }

  return (
    <div className="ax-glass p-6 lg:p-7" data-testid={`cms-editor-${page}-${section}`}>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="ax-serif text-2xl">{title}</h3>
            <span
              className={`ax-chip ${doc?.is_published ? "ax-chip-success" : "ax-chip-warn"}`}
              data-testid={`cms-status-${page}-${section}`}
            >
              {doc?.is_published ? "Published" : "Not published yet"}
            </span>
          </div>
          {description && (
            <p className="text-sm mt-2" style={{ color: "var(--ax-text-muted)" }}>
              {description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="ax-btn ax-btn-ghost" onClick={() => setShowHistory((v) => !v)} data-testid={`cms-history-toggle-${page}-${section}`}>
            <History className="w-3.5 h-3.5" /> History
          </button>
          <button type="button" className="ax-btn ax-btn-ghost" onClick={discard} data-testid={`cms-discard-${page}-${section}`}>
            <RotateCcw className="w-3.5 h-3.5" /> Discard
          </button>
          <button type="button" className="ax-btn ax-btn-ghost" onClick={() => saveDraft(false)} disabled={saving} data-testid={`cms-save-draft-${page}-${section}`}>
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save draft"}
          </button>
          <button type="button" className="ax-btn ax-btn-gold" onClick={publish} disabled={publishing} data-testid={`cms-publish-${page}-${section}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      {showHistory && (
        <div className="mb-6 rounded-xl p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--ax-border)" }} data-testid={`cms-history-panel-${page}-${section}`}>
          <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "var(--ax-text-muted)" }}>
            Version history
          </p>
          {!doc?.history || doc.history.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--ax-text-muted)" }}>
              No previous versions yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {doc.history.map((h, i) => {
                const diff = diffFields(h.content, draft);
                return (
                  <li key={i} className="rounded-lg" style={{ border: "1px solid var(--ax-border)" }}>
                    <div className="flex items-center justify-between text-sm p-3">
                      <span style={{ color: "var(--ax-text-dim)" }}>{new Date(h.published_at).toLocaleString("en-IN")}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="ax-btn ax-btn-ghost"
                          onClick={() => setPreviewIndex(previewIndex === i ? null : i)}
                          data-testid={`cms-preview-${page}-${section}-${i}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> Preview
                        </button>
                        <button type="button" className="ax-btn ax-btn-ghost" onClick={() => restore(i)} data-testid={`cms-restore-${page}-${section}-${i}`}>
                          Restore
                        </button>
                      </div>
                    </div>
                    {previewIndex === i && (
                      <div className="px-3 pb-3" data-testid={`cms-diff-${page}-${section}-${i}`}>
                        {diff.length === 0 ? (
                          <p className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
                            No differences from your current draft.
                          </p>
                        ) : (
                          <div className="space-y-1.5 text-xs">
                            {diff.map((d) => (
                              <div key={d.key} className="flex flex-wrap gap-1.5">
                                <span className="font-medium" style={{ color: "var(--ax-text-dim)" }}>{d.key}:</span>
                                <span className="text-red-400/80 line-through">{d.from}</span>
                                <span style={{ color: "var(--ax-text-muted)" }}>→</span>
                                <span style={{ color: "var(--ax-gold)" }}>{d.to}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className="space-y-5">{renderFields(draft, setField, setDraft)}</div>
    </div>
  );
}
